// ---------------------------------------------------------------------------
// Timeframe presets + range resolution (pure; no React, no DOM).
//
// WHY THIS FILE EXISTS
// --------------------
// The preset table and window resolution used to live inside FilterContext.jsx,
// which imports React and JSX — so `node --test` could not import it and the
// rolling-window math was only ever checked by hand in a browser. M-FIX moves
// it here as a pure module (mirroring lib/insights.js and lib/timeseries.js) so
// `npm test` can prove the rolling/calendar semantics directly.
//
// ROLLING presets are TRUE rolling windows measured backwards from "now" at hour
// granularity (e.g. "Last 24 hours" = now − 24h → now), emitted to the API as
// ISO-8601 UTC datetimes. CALENDAR presets are anchored to UTC period boundaries
// (month/quarter/year start) and are explicitly labelled "(period)" so a reader
// is never misled into expecting a rolling window. The old "Today (24h)" meant
// today-since-UTC-midnight — a PARTIAL day, and almost empty just after midnight
// UTC — and there was no true rolling 24-hour option at all.
// ---------------------------------------------------------------------------

export const RANGE_PRESETS = [
  { id: 'last24h', label: 'Last 24 hours', hours: 24, rolling: true },
  { id: 'last7d', label: 'Last 7 days', hours: 168, rolling: true },
  { id: 'last14d', label: 'Last 14 days', hours: 336, rolling: true },
  { id: 'last30d', label: 'Last 30 days', hours: 720, rolling: true },
  { id: 'last90d', label: 'Last 90 days', hours: 2160, rolling: true },
  { id: 'mtd', label: 'Month to date (period)', period: true },
  { id: 'qtd', label: 'Quarter to date (period)', period: true },
  { id: 'ytd', label: 'Year to date (period)', period: true },
  { id: 'l12m', label: 'Last 12 months (period)', period: true },
  { id: 'custom', label: 'Custom range…' },
];

// Rolling preset id → window length in hours.
const ROLLING_HOURS = Object.fromEntries(
  RANGE_PRESETS.filter((p) => p.rolling).map((p) => [p.id, p.hours])
);

// Legacy numeric preset ids (pre-M-FIX) map onto the new rolling ids so any
// stale caller / deep link still resolves instead of showing an empty control.
const LEGACY_PRESET_ALIASES = {
  1: 'last24h',
  7: 'last7d',
  14: 'last14d',
  30: 'last30d',
  90: 'last90d',
};

export const DEFAULT_PRESET = 'last7d';

export function normalizePresetId(id) {
  if (id == null) return DEFAULT_PRESET;
  return LEGACY_PRESET_ALIASES[id] || String(id);
}

// Backward-compatible export (older consumers referenced DATE_OPTIONS as a
// `{value: days, label}` list).
export const DATE_OPTIONS = RANGE_PRESETS.filter((p) => p.rolling).map((p) => ({
  value: p.hours / 24,
  label: p.label,
}));

// Analytics Engine retains ~90 days. Windows longer than this are still
// requestable but the server clamps them; we flag them here so the UI can label
// the truncation immediately (see functions/_lib/range.js for the reasoning).
export const AE_RETENTION_DAYS = 90;

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

function iso(d) {
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr, delta) {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return iso(d);
}

function diffDays(a, b) {
  return Math.round(
    (new Date(`${b}T00:00:00.000Z`).getTime() - new Date(`${a}T00:00:00.000Z`).getTime()) /
      MS_PER_DAY
  );
}

/**
 * Resolve a preset (+ optional custom from/to) into a concrete range object.
 *
 * Rolling presets emit ISO-8601 UTC datetimes (hour-granular, exclusive end =
 * "now"); calendar presets emit inclusive `YYYY-MM-DD` bounds. `hours` is set
 * for rolling windows so the UI can render "24h" instead of "1d".
 *
 * @returns {{
 *   preset: string, from: string, to: string, days: number, hours: number|null,
 *   grain: 'day'|'instant', truncated: boolean,
 *   compareFrom: string, compareTo: string
 * }}
 */
export function resolveRange(preset, customFrom, customTo, now = new Date()) {
  const id = normalizePresetId(preset);
  const nowMs = now.getTime();
  const today = iso(now);

  let from;
  let to;
  let days;
  let hours = null;
  let grain = 'day';

  if (ROLLING_HOURS[id]) {
    hours = ROLLING_HOURS[id];
    const dur = hours * MS_PER_HOUR;
    from = new Date(nowMs - dur).toISOString();
    to = new Date(nowMs).toISOString();
    days = Math.round(hours / 24);
    grain = 'instant';
  } else {
    switch (id) {
      case 'mtd':
        from = `${today.slice(0, 7)}-01`;
        to = today;
        break;
      case 'qtd': {
        const qStartMonth = Math.floor(now.getUTCMonth() / 3) * 3 + 1;
        from = `${now.getUTCFullYear()}-${String(qStartMonth).padStart(2, '0')}-01`;
        to = today;
        break;
      }
      case 'ytd':
        from = `${now.getUTCFullYear()}-01-01`;
        to = today;
        break;
      case 'l12m': {
        const d = new Date(`${today}T00:00:00.000Z`);
        d.setUTCDate(1);
        d.setUTCMonth(d.getUTCMonth() - 11);
        from = iso(d);
        to = today;
        break;
      }
      case 'custom':
        from = customFrom || addDays(today, -6);
        to = customTo || today;
        break;
      default: {
        // Unknown id — fall back to the default rolling 7-day window.
        hours = ROLLING_HOURS[DEFAULT_PRESET];
        from = new Date(nowMs - hours * MS_PER_HOUR).toISOString();
        to = new Date(nowMs).toISOString();
        days = Math.round(hours / 24);
        grain = 'instant';
      }
    }
    if (grain === 'day') {
      if (from > to) [from, to] = [to, from];
      days = diffDays(from, to) + 1;
    }
  }

  // Prior equal-length window immediately preceding the current one.
  let compareFrom;
  let compareTo;
  if (grain === 'instant') {
    const dur = hours * MS_PER_HOUR;
    const fromMs = new Date(from).getTime();
    compareTo = from; // exclusive end = current window start
    compareFrom = new Date(fromMs - dur).toISOString();
  } else {
    compareFrom = addDays(from, -days);
    compareTo = addDays(from, -1);
  }

  return {
    preset: id,
    from,
    to,
    days,
    hours,
    grain,
    truncated: days > AE_RETENTION_DAYS,
    compareFrom,
    compareTo,
  };
}
